import { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Typography,
  Table,
  TableHead,
  TableSortLabel,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ViewModuleOutlinedIcon from '@material-ui/icons/ViewModuleOutlined';
import ViewListOutlinedIcon from '@material-ui/icons/ViewListOutlined';

import { formatDate, sequence, useSessionStorage } from '../../../common/utils';
import SatelliteImageThumbnail from './SatelliteImageThumbnail';

/**
 * @constant CARD_MEDIA_HEIGHT
 * @description Height, in px, of a grid card's thumbnail area. Fixed, so every card in a row is
 * the same height whatever shape the imagery turns out to be - the width is what adapts instead.
 * @memberof module:SHARED
 */
const CARD_MEDIA_HEIGHT = 150;

/**
 * @constant IMAGE_VIEW_STORAGE_KEY
 * @description Where the chosen view is remembered for the rest of the visit.
 * @memberof module:SHARED
 */
const IMAGE_VIEW_STORAGE_KEY = 'acquisitionImageListView';

/**
 * @constant CARD_MIN_WIDTH
 * @description Narrowest a grid card may get, in px, whatever the imagery asks for. Below this the
 * date and the figures under the thumbnail start to wrap.
 * @memberof module:SHARED
 */
const CARD_MIN_WIDTH = 140;

/**
 * @constant CARD_MAX_WIDTH
 * @description Widest a grid card may get, in px. Caps how much a very wide scene can take, so a
 * long, thin AOI still leaves more than two cards per row.
 * @memberof module:SHARED
 */
const CARD_MAX_WIDTH = 280;

/**
 * @constant DEFAULT_THUMBNAIL_ASPECT
 * @description Width-to-height ratio assumed until the first thumbnail has been measured, so the
 * grid has a sensible column width to lay out with on its very first paint.
 * @memberof module:SHARED
 */
const DEFAULT_THUMBNAIL_ASPECT = 4 / 3;

/**
 * @function useThumbnailAspect
 * @description Reports the width-to-height ratio of a thumbnail, so the cards showing it can be
 * cut to the shape of the imagery rather than to a guess. Earth Engine renders these to a fixed
 * largest dimension over the AOI's own outline, so a tall, narrow region yields tall, narrow
 * images - which is why one fixed card shape leaves either empty margins or a heavy crop.
 * Measured from a single probe: every scene in a result set is rendered over the same AOI, so they
 * share a ratio, and one measurement settles the whole grid. The probe reads a URL the cards are
 * already showing, so the browser serves it from cache and nothing extra is fetched.
 * @param {String} [url] - Thumbnail to measure. Falsy leaves the ratio unknown.
 * **Called by:
 * @see {ImageTable} - for its grid view.
 * @memberof module:SHARED
 * @returns {Number|null} The ratio, or `null` while it is not known yet.
 */
const useThumbnailAspect = (url) => {
  const [aspect, setAspect] = useState(null);

  useEffect(() => {
    if (!url) return undefined;

    let active = true;
    const probe = new Image();
    probe.onload = () => {
      if (active && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.src = url;

    return () => {
      active = false;
    };
  }, [url]);

  return aspect;
};

const useStyles = makeStyles((theme) => ({
  /**
   * Modern table. The same rows, lightened: the header reads as a quiet label strip rather than a
   * ruled band, the cell dividers step back to the theme divider, and the whole thing sits in a
   * rounded outline so it matches the cards the grid view shows for the same data.
   */
  tableToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  tableModern: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    borderCollapse: 'separate',
    borderSpacing: 0,
    overflow: 'hidden',
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'none',
    },
    '& .MuiTableFooter-root .MuiTableCell-root': {
      borderBottom: 0,
    },
  },
  gridToolbar: {
    display: 'flex',
    padding: theme.spacing(0, 1),
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  gridSorters: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  gridSorter: {
    fontSize: 13,
  },
  /**
   * Grid variant. Each image becomes a card with its thumbnail, date and figures instead of a table
   * row: the thumbnail is what the choice is actually made on, and at row height it was too small
   * to judge cloud cover by eye. The whole card toggles selection, as the row already did.
   * How many fit in a row is left to the available width rather than fixed per breakpoint:
   * `auto-fill` takes as many columns as the panel can hold at the width the imagery itself asks
   * for (see {@link useThumbnailAspect}), so wide scenes get wide cards and narrow ones pack more
   * into the same space, with no list of breakpoints to keep in step with the card contents.
   */
  gridContainer: {
    display: 'grid',
    gap: 8,
    alignItems: 'start',
    /**
     * A gutter of its own on both sides. The cards stretch to the column they are laid into, so
     * without it the outer ones sat flush against the panel edge while everything above them - the
     * toolbar, the stepper buttons - kept its own inset.
     */
    padding: theme.spacing(0, 1),
  },
  imageCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  imageCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  /**
   * The thumbnail fills the card's whole width, at a card width chosen from the imagery's own
   * proportions, so `object-fit: cover` has next to nothing to crop and the card has no empty
   * margins to show. Every wrapper between the card and the image has to be opened up for that to
   * reach it: the thumbnail's own Box, and the two the zoom library inserts.
   * Pointer events are off on the image so a click anywhere on the card selects it. The image
   * carries a `stopPropagation` handler of its own (it opens the zoom overlay), which swallowed
   * clicks on the largest part of the card - selection appeared not to register the first time.
   * The zoom stays available in the list view, where the image is not the click target.
   */
  imageCardMedia: {
    padding: theme.spacing(0, 1),
    height: CARD_MEDIA_HEIGHT,
    '& div, & span': {
      width: '100%',
      height: '100%',
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 6,
      pointerEvents: 'none',
    },
  },
  imageCardHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0.5, 1, 0),
  },
  imageCardCheckbox: {
    padding: 2,
  },
  /**
   * Centred, and every line of it on the same axis as the thumbnail above: the cards are narrow
   * enough that a left-aligned label with its figure pushed to the opposite edge read as two
   * columns rather than as one card.
   */
  imageCardBody: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 2,
  },
  imageCardTitle: {
    fontWeight: 600,
  },
  imageCardSubtitle: {
    color: theme.palette.text.secondary,
  },
  imageCardFacts: {
    marginTop: 6,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: 10,
    rowGap: 2,
  },
  imageCardFact: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
  },
  imageCardFactLabel: {
    color: theme.palette.text.secondary,
  },
  imageCardFactValue: {
    fontWeight: 500,
  },
  rowClickable: {
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

/**
 * @function ImageCard
 * @description One image as a card: its thumbnail, its date and the figures the choice is made on.
 * Kept apart from the list and memoised because the thumbnails arrive one at a time, six requests
 * in flight, and each arrival re-renders the list that holds the cards. Redrawing every card on
 * each of those - thumbnail, zoom wrapper and all - is what made selecting an image while the rest
 * were still loading feel unresponsive. A card now re-renders when something it shows changes.
 * @param {String} props.imageKey - The image's key, as the list builds it.
 * @param {Object} props.classes - The list's own JSS classes, stable across its renders.
 * @param {Boolean} props.selected - Whether this image is chosen.
 * @param {String} props.thumbnail - Thumbnail URL, absent until it arrives.
 * @param {String} props.title - The capture date, formatted.
 * @param {String} props.subtitle - The mission's short name.
 * @param {Array<Object>} props.facts - Label/value pairs shown under the thumbnail.
 * @param {Function} props.onToggle - Called with the key and the next selected state; must keep its
 *  identity between renders or the card can never skip one.
 * **Called by:
 * @see {ImageTable} - in its grid view.
 * @memberof module:SHARED
 * @returns {JSX.Element} The card.
 */
const ImageCard = memo(
  ({ imageKey, classes, selected, thumbnail, title, subtitle, facts, onToggle }) => (
    <Card
      className={[classes.imageCard, selected && classes.imageCardSelected].filter(Boolean).join(' ')}
      onClick={() => onToggle(imageKey, !selected)}>
      <div className={classes.imageCardHeader}>
        <Checkbox
          className={classes.imageCardCheckbox}
          checked={selected}
          color="primary"
          size="small"
          onChange={(e) => onToggle(imageKey, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          inputProps={{ 'aria-label': `${subtitle} ${title}` }}
        />
      </div>
      <div className={classes.imageCardMedia}>
        <SatelliteImageThumbnail url={thumbnail} height={CARD_MEDIA_HEIGHT} />
      </div>
      <div className={classes.imageCardBody}>
        <Typography variant="body2" className={classes.imageCardTitle}>
          {title}
        </Typography>
        <Typography variant="caption" className={classes.imageCardSubtitle}>
          {subtitle}
        </Typography>
        <div className={classes.imageCardFacts}>
          {facts.map((fact) => (
            <div className={classes.imageCardFact} key={fact.label}>
              <Typography variant="caption" className={classes.imageCardFactLabel}>
                {fact.label}
              </Typography>
              <Typography variant="caption" className={classes.imageCardFactValue}>
                {fact.value}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </Card>
  ),
  (previous, next) =>
    previous.imageKey === next.imageKey &&
    previous.selected === next.selected &&
    previous.thumbnail === next.thumbnail &&
    previous.title === next.title &&
    previous.subtitle === next.subtitle &&
    previous.onToggle === next.onToggle &&
    previous.classes === next.classes &&
    previous.facts.length === next.facts.length &&
    previous.facts.every((fact, i) => fact.label === next.facts[i].label && fact.value === next.facts[i].value),
);

const ImageTable = ({
  images = [],
  metadata = [],
  selected = {},
  onCheckboxChange = () => {},
  onSelectAll = () => {},
  variant = 'table',
}) => {
  const [t] = useTranslation();
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);

  const [sorted, setSorted] = useState(() => sequence(images.length, 0));
  const [orderIndex, setOrderIndex] = useState(null);
  const [order, setOrder] = useState('asc');

  const [prevImagesLength, setPrevImagesLength] = useState(images.length);
  if (images.length !== prevImagesLength) {
    setPrevImagesLength(images.length);
    setSorted(sequence(images.length, 0));
    setPage(0);
  }

  const toDateStr = useCallback((d) => {
    return typeof d === 'string' ? d : (d?.toISOString?.() ?? String(d));
  }, []);

  const getImageKey = useCallback(
    (img) => {
      const date = toDateStr(img?.date);
      const name = img?.name ?? img?.shortname ?? '';
      return img?.id ?? `${date}-${name}`;
    },
    [toDateStr],
  );

  const tideData = useMemo(() => JSON.parse(localStorage.getItem('tideData')) || [], []);

  const formatCloudPercentage = useCallback(
    (index) => {
      if (images[index]?.content === -1) return '-';
      return (images[index].content * 100).toFixed(1) + '%';
    },
    [images],
  );

  const metadataMap = useMemo(() => {
    return (metadata || []).reduce((acc, m) => {
      acc[`${m.date}-${m.missionName}`] = m;
      return acc;
    }, {});
  }, [metadata]);

  const getThumbnail = useCallback(
    (image) => {
      return metadataMap[`${image.date}-${image.name}`]?.thumbnail;
    },
    [metadataMap],
  );

  const tideByDate = useMemo(() => {
    if (!tideData.length) return {};

    const timezone = Number(localStorage.getItem('timezone')) || 0;
    const MAX_DIFF_MS = 3 * 60 * 60 * 1000;

    const parseTide = (str) => {
      const [datePart, timePart] = str.split(' ');
      const [Y, M, D] = datePart.split('-').map(Number);
      const [h, m, s] = timePart.split(':').map(Number);
      return new Date(Y, M - 1, D, h + timezone, m, s);
    };

    return images.reduce((acc, img) => {
      const target = new Date(img.date);

      let bestDiff = Infinity;
      let bestValue = null;

      tideData.forEach(([dateStr, value]) => {
        const diff = Math.abs(parseTide(dateStr) - target);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestValue = value;
        }
      });

      const dateStr = toDateStr(img.date);
      acc[dateStr] = bestDiff <= MAX_DIFF_MS ? bestValue : null;
      return acc;
    }, {});
  }, [images, tideData, toDateStr]);

  const hasAnyTideData = useMemo(() => Object.values(tideByDate).some((v) => v !== null), [tideByDate]);

  const COLUMNS = useMemo(() => {
    const cols = [
      {
        label: t('forms.acquisition.4.table.id'),
        selector: (index) => `${images[index].shortname} - ${formatDate(images[index].date, true)}`,
        getValue: (index) => `${images[index].shortname}-${toDateStr(images[index].date)}`,
      },
      {
        label: t('forms.acquisition.4.table.cloud'),
        selector: (index) => formatCloudPercentage(index),
        getValue: (index) => images[index].content,
        inCardFacts: true,
      },
      {
        label: t('forms.acquisition.4.table.thumbnail'),
        selector: (index) => <SatelliteImageThumbnail url={getThumbnail(images[index])} height={125} />,
        getValue: () => 0,
        sortable: false,
      },
    ];

    if (hasAnyTideData) {
      cols.push({
        label: t('forms.acquisition.4.table.tide'),
        selector: (index) => {
          const d = toDateStr(images[index].date);
          const raw = tideByDate[d];
          if (raw === undefined || raw === null) return t('forms.acquisition.4.table.noData');
          const num = Number(raw);
          return Number.isFinite(num) ? num.toFixed(2) : raw;
        },
        getValue: (index) => {
          const d = toDateStr(images[index].date);
          return tideByDate[d] ?? -999999;
        },
        inCardFacts: true,
      });
    }

    return cols;
  }, [images, t, formatCloudPercentage, getThumbnail, tideByDate, hasAnyTideData, toDateStr]);

  const selectedCount = useMemo(() => {
    return images.reduce((acc, img) => acc + (selected?.[getImageKey(img)] ? 1 : 0), 0);
  }, [images, selected, getImageKey]);

  const allChecked = useMemo(() => {
    if (!images.length) return 0;
    if (selectedCount === 0) return 0;
    if (selectedCount === images.length) return 1;
    return -1;
  }, [images.length, selectedCount]);

  const handleSort = useCallback(
    (index) => {
      const col = COLUMNS[index];
      const ordering = index === orderIndex && order === 'desc' ? 'asc' : 'desc';

      const next = sequence(images.length, 0);
      const retrieveValue = col.getValue || col.selector;

      next.sort((a, b) => {
        const va = retrieveValue(a);
        const vb = retrieveValue(b);

        const val = va < vb ? 1 : va > vb ? -1 : 0;
        return ordering === 'desc' ? val : -val;
      });

      setSorted(next);
      setOrder(ordering);
      setOrderIndex(index);
    },
    [COLUMNS, images.length, order, orderIndex],
  );

  const handleCheckAll = useCallback(
    (checked) => {
      const keys = images.map(getImageKey);
      onSelectAll(keys, checked);
    },
    [images, getImageKey, onSelectAll],
  );

  /**
   * A selection handler that keeps its identity, whatever the caller passes. Every caller writes its
   * own inline, so a card handed that prop directly would see a new function on every render and
   * could never skip one.
   */
  const onCheckboxChangeRef = useRef(onCheckboxChange);
  onCheckboxChangeRef.current = onCheckboxChange;
  const handleToggle = useCallback((key, checked) => onCheckboxChangeRef.current(key, checked), []);

  /**
   * The figures under one card's thumbnail, as plain label/value pairs the card can compare against
   * what it last drew.
   */
  const cardFacts = useCallback(
    (index) => COLUMNS.filter((col) => col.inCardFacts).map((col) => ({ label: col.label, value: col.selector(index) })),
    [COLUMNS],
  );

  const isModern = variant === 'modern';
  /**
   * Which of the two the list opens as. The list, always: it is the denser of the two and the one
   * the flow has always opened with, so nobody arrives to a layout they did not ask for.
   * Switching to the grid holds for the rest of the visit - walking to the next step and back does
   * not undo it - but it is not carried into the next one, which is what `sessionStorage` buys over
   * `localStorage` here. A grid chosen once for one area would otherwise be what greeted the user
   * on every analysis from then on.
   */
  const [view, setView] = useSessionStorage(IMAGE_VIEW_STORAGE_KEY, 'table');
  const isGrid = isModern && view === 'grid';

  const firstThumbnail = useMemo(() => {
    const withThumbnail = images.find((image) => Boolean(getThumbnail(image)));
    return withThumbnail ? getThumbnail(withThumbnail) : null;
  }, [images, getThumbnail]);

  const measuredAspect = useThumbnailAspect(isGrid ? firstThumbnail : null);

  /**
   * A card is as wide as its thumbnail wants to be at {@link CARD_MEDIA_HEIGHT}, within bounds that
   * keep the text below it readable and keep a row from ever holding a single card.
   */
  const cardWidth = useMemo(() => {
    const aspect = measuredAspect || DEFAULT_THUMBNAIL_ASPECT;
    return Math.min(CARD_MAX_WIDTH, Math.max(CARD_MIN_WIDTH, Math.round(CARD_MEDIA_HEIGHT * aspect)));
  }, [measuredAspect]);

  const start = page * rows;

  const visibleRows = sorted.filter((idx) => idx < images.length).slice(start, start + rows);

  const viewToggle = isModern ? (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={view}
      onChange={(event, next) => next && setView(next)}
      aria-label={t('forms.acquisition.4.table.viewMode')}>
      <ToggleButton value="grid" aria-label={t('forms.acquisition.4.table.viewGrid')}>
        <ViewModuleOutlinedIcon fontSize="small" />
      </ToggleButton>
      <ToggleButton value="table" aria-label={t('forms.acquisition.4.table.viewList')}>
        <ViewListOutlinedIcon fontSize="small" />
      </ToggleButton>
    </ToggleButtonGroup>
  ) : null;
  const pagination = (
    <TablePagination
      component="div"
      count={images.length}
      rowsPerPage={rows}
      page={page}
      rowsPerPageOptions={[5, 10, 15, 20, 50, 100, 200]}
      onPageChange={(e, p) => setPage(p)}
      onRowsPerPageChange={(e) => {
        setRows(Number(e.target.value));
        setPage(0);
      }}
    />
  );

  if (isGrid) {
    return (
      <Box>
        <Box className={classes.gridToolbar}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allChecked === 1}
                indeterminate={allChecked === -1}
                onChange={(e) => handleCheckAll(e.target.checked)}
                color="primary"
              />
            }
            label={<Typography variant="body2">{t('forms.acquisition.4.table.selected')}</Typography>}
          />
          <Box className={classes.gridSorters}>
            {viewToggle}
            {COLUMNS.map((col, i) =>
              col.sortable === false ? null : (
                <TableSortLabel
                  key={col.label}
                  className={classes.gridSorter}
                  active={orderIndex === i}
                  direction={orderIndex === i ? order : 'asc'}
                  onClick={() => handleSort(i)}>
                  {col.label}
                </TableSortLabel>
              ),
            )}
          </Box>
        </Box>

        <div
          className={classes.gridContainer}
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardWidth}px, 1fr))` }}>
          {visibleRows.map((idx) => {
            const img = images[idx];
            const key = getImageKey(img);
            return (
              <ImageCard
                key={key}
                imageKey={key}
                classes={classes}
                selected={!!selected?.[key]}
                thumbnail={getThumbnail(img)}
                title={formatDate(img.date, true)}
                subtitle={img.shortname}
                facts={cardFacts(idx)}
                onToggle={handleToggle}
              />
            );
          })}
        </div>
        {pagination}
      </Box>
    );
  }
  return (
    <Box>
      {isModern && <Box className={classes.tableToolbar}>{viewToggle}</Box>}
      <Table className={isModern ? classes.tableModern : undefined} size={isModern ? 'small' : 'medium'}>
      <TableHead>
        <TableRow>
          {COLUMNS.map((col, i) => (
            <TableCell key={i} sortDirection={orderIndex === i ? order : false}>
              <TableSortLabel
                active={orderIndex === i}
                direction={orderIndex === i ? order : 'asc'}
                onClick={() => handleSort(i)}>
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}

          <TableCell>
            {t('forms.acquisition.4.table.selected')}
            <Checkbox
              checked={allChecked === 1}
              indeterminate={allChecked === -1}
              onChange={(e) => handleCheckAll(e.target.checked)}
            />
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {visibleRows.map((idx) => {
          const img = images[idx];
          const key = getImageKey(img);

          return (
            <TableRow
              key={key}
              className={classes.rowClickable}
              onClick={() => onCheckboxChange(key, !selected?.[key])}
            >
              {COLUMNS.map((col, i) => (
                <TableCell key={i}>{col.selector(idx)}</TableCell>
              ))}

              <TableCell>
                <Checkbox
                  checked={!!selected?.[key]}
                  onChange={(e) => onCheckboxChange(key, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>

      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={COLUMNS.length + 1}
            count={images.length}
            rowsPerPage={rows}
            page={page}
            rowsPerPageOptions={[5, 10, 15, 20, 50, 100, 200]}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRows(Number(e.target.value));
              setPage(0);
            }}
          />
        </TableRow>
      </TableFooter>
      </Table>
    </Box>
  );
};

export default ImageTable;
